import React, { useCallback, useEffect, useState } from "react";
import { Button, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import ElectricityApartmentAgreementsService, {
  ApartmentAgreement,
  ApartmentAgreementStatus,
} from "services/electricity/electricity-apartment-agreements.service";
import AddApartmentForm from "./AddApartmentForm";
import RenameApartmentForm from "./RenameApartmentForm";
import ChangeApartmentCustomerForm from "./ChangeApartmentCustomerForm";
import EndApartmentForm from "./EndApartmentForm";
import CancelApartmentForm from "./CancelApartmentForm";
import { ELECTRICITY_AGREEMENT_STATUS_MAP } from "../models";
import { formatDate } from "common/dateUtil";

interface Props {
  agreementId: number;
  defaultDateStart?: string;
  parentDateEnd?: string;
}

type RowAction = "rename" | "change-customer" | "end" | "cancel";

const todayIso = (): string => new Date().toISOString().slice(0, 10);

export default function ApartmentAgreementsBlock({ agreementId, defaultDateStart, parentDateEnd }: Props) {
  const [rows, setRows] = useState<ApartmentAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [openRow, setOpenRow] = useState<{ id: number; action: RowAction } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ElectricityApartmentAgreementsService.listByAgreementId(agreementId, true);
      setRows(data ?? []);
    } catch (e) {
      toast.error("Korterite laadimine ebaõnnestus");
    } finally {
      setLoading(false);
    }
  }, [agreementId]);

  useEffect(() => {
    load();
  }, [load]);

  const onSavedAdd = () => {
    setAdding(false);
    load();
  };

  const onSavedRow = () => {
    setOpenRow(null);
    load();
  };

  const closeRow = () => setOpenRow(null);

  const toggleRow = (id: number, action: RowAction) => {
    setOpenRow((prev) =>
      prev && prev.id === id && prev.action === action ? null : { id, action }
    );
  };

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">Korterid</h2>
        <Button
          variant="link"
          className="p-0"
          onClick={() => setAdding((v) => !v)}
          disabled={adding}
        >
          + Lisa korter
        </Button>
      </div>

      {adding && (
        <AddApartmentForm
          agreementId={agreementId}
          defaultDateStart={defaultDateStart}
          parentDateEnd={parentDateEnd}
          onSaved={onSavedAdd}
          onCancel={() => setAdding(false)}
        />
      )}

      {loading ? (
        <Spinner animation="border" />
      ) : rows.length === 0 ? (
        <p>Korterid puuduvad.</p>
      ) : (
        <Table striped hover size="sm">
          <thead>
            <tr>
              <th>Korter</th>
              <th>Klient</th>
              <th>Algus</th>
              <th>Lõpp</th>
              <th>Staatus</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isActive = r.status === ApartmentAgreementStatus.ACTIVE;
              const isFuture = r.startDate > todayIso();
              const isOpen = openRow?.id === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr>
                    <td>{r.name}</td>
                    <td>{r.customerName ?? r.customerId}</td>
                    <td>{formatDate(r.startDate)}</td>
                    <td>{formatDate(r.endDate)}</td>
                    <td>{ELECTRICITY_AGREEMENT_STATUS_MAP[r.status]}</td>
                    <td className="text-right">
                      {isActive && (
                        <>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="p-0 mr-2"
                            onClick={() => toggleRow(r.id, "rename")}
                          >
                            Nimeta ümber
                          </Button>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="p-0 mr-2"
                            onClick={() => toggleRow(r.id, "change-customer")}
                          >
                            Vaheta klient
                          </Button>
                          {isFuture ? (
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="p-0 text-danger"
                              onClick={() => toggleRow(r.id, "cancel")}
                            >
                              Tühista
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="p-0 text-danger"
                              onClick={() => toggleRow(r.id, "end")}
                            >
                              Lõpeta
                            </Button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={6} className="bg-white">
                        {openRow?.action === "rename" && (
                          <RenameApartmentForm apartment={r} onSaved={onSavedRow} onCancel={closeRow} />
                        )}
                        {openRow?.action === "change-customer" && (
                          <ChangeApartmentCustomerForm apartment={r} parentDateEnd={parentDateEnd} onSaved={onSavedRow} onCancel={closeRow} />
                        )}
                        {openRow?.action === "end" && (
                          <EndApartmentForm apartment={r} parentDateEnd={parentDateEnd} onSaved={onSavedRow} onCancel={closeRow} />
                        )}
                        {openRow?.action === "cancel" && (
                          <CancelApartmentForm apartment={r} onSaved={onSavedRow} onCancel={closeRow} />
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}
